import React from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';

/**
 * Branches, departments, sub-departments, users — aligned with document access.
 * Listed users grant access explicitly. Org scope grants use one tier only: if sub-departments are selected, only those apply (department/branch selects are ignored for persistence); department-only excludes branch grants; branch-only stays at branch level.
 * Parent should not render this section when the user cannot manage sharing (field-only editors).
 */
const ACCESS_OPTIONS = [
  { value: 'view', label: 'View' },
  { value: 'edit', label: 'Edit details' },
  { value: 'full', label: 'Full access' },
];

/** Branch / department / sub-department only (includes explicit deny for org-scope matches). */
const SCOPE_ACCESS_OPTIONS = [
  { value: 'no_access', label: 'No access' },
  ...ACCESS_OPTIONS,
];

function userListIdEquals(u, selectedId) {
  const a = Number(u?.id ?? u?.CorporateUserId);
  const b = Number(selectedId);
  return Number.isFinite(a) && Number.isFinite(b) && a === b;
}

const CredentialAccessSection = ({
  selectedBranches = [],
  onBranchesChange,
  selectedDepartments = [],
  onDepartmentsChange,
  selectedSubDepartments = [],
  onSubDepartmentsChange,
  branches = [],
  departments = [],
  subDepartments = [],
  filteredDepartments = [],
  filteredSubDepartments = [],
  availableUsers = [],
  selectedUsers = [],
  onUsersChange,
  userAccessById = {},
  onUserAccessLevelChange,
  branchAccessById = {},
  departmentAccessById = {},
  subDepartmentAccessById = {},
  onBranchAccessLevelChange,
  onDepartmentAccessLevelChange,
  onSubDepartmentAccessLevelChange,
  /** Optional content directly under the "Access control" heading (e.g. corporate-only private checkbox). */
  prepend = null,
  /** When false, only the heading and prepend are shown (no org/user sharing controls). */
  showSharingControls = true,
}) => {
  const accent = '#c42126';
  const accentSoft = 'rgba(196, 33, 38, 0.22)';

  const customStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? accent : '#e9ecef',
      boxShadow: state.isFocused ? `0 0 0 0.2rem ${accentSoft}` : 'none',
      minHeight: '49px',
      height: '49px',
      borderRadius: '0.65rem',
      '&:hover': {
        borderColor: state.isFocused ? accent : '#ced4da',
      },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? accent : state.isFocused ? 'rgba(15, 23, 42, 0.04)' : 'white',
      color: state.isSelected ? 'white' : '#495057',
      '&:active': {
        backgroundColor: accent,
        color: 'white',
      },
    }),
    placeholder: (base) => ({
      ...base,
      color: '#868e96',
    }),
  };

  const handleBranchChange = (selectedOptions) => {
    const branchIds = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
    if (onBranchesChange) onBranchesChange(branchIds, selectedOptions || []);
  };

  const handleDepartmentChange = (selectedOptions) => {
    const departmentIds = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
    if (onDepartmentsChange) onDepartmentsChange(departmentIds, selectedOptions || []);
  };

  const handleSubDepartmentChange = (selectedOptions) => {
    const subDepartmentIds = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
    if (onSubDepartmentsChange) onSubDepartmentsChange(subDepartmentIds, selectedOptions || []);
  };

  const handleUserChange = (selectedOptions) => {
    const userIds = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
    if (onUsersChange) onUsersChange(userIds, selectedOptions || []);
  };

  const deptOptions = filteredDepartments.length > 0 ? filteredDepartments : departments;
  const subOptions = filteredSubDepartments.length > 0 ? filteredSubDepartments : subDepartments;

  const showBranchScopeLevels =
    selectedBranches.length > 0 && selectedDepartments.length === 0 && selectedSubDepartments.length === 0;
  const showDepartmentScopeLevels =
    selectedDepartments.length > 0 && selectedSubDepartments.length === 0;
  const showSubScopeLevels = selectedSubDepartments.length > 0;

  return (
    <div className="mt-4 pt-4 credential-access-section">
      <h6 className="credential-access-section__heading">Access control</h6>
      {prepend ? <div className="credential-access-section__prepend mb-3">{prepend}</div> : null}

      {showSharingControls ? (
        <Row>
        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="text-label">Branches</Form.Label>
            <Select
              isMulti
              options={branches.map((branch) => ({
                value: branch.id,
                label: branch.name,
                data: branch,
              }))}
              value={selectedBranches
                .map((id) => {
                  const branch = branches.find((b) => b.id === id);
                  return branch ? { value: branch.id, label: branch.name, data: branch } : null;
                })
                .filter(Boolean)}
              onChange={handleBranchChange}
              isClearable
              placeholder="Add branches…"
              styles={customStyles}
            />
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="text-label">Departments</Form.Label>
            <Select
              isMulti
              options={deptOptions.map((dept) => ({
                value: dept.id,
                label: dept.branch_name ? `${dept.name} (${dept.branch_name})` : dept.name,
                data: dept,
              }))}
              value={selectedDepartments
                .map((id) => {
                  const dept = deptOptions.find((d) => d.id === id);
                  return dept
                    ? {
                        value: dept.id,
                        label: dept.branch_name ? `${dept.name} (${dept.branch_name})` : dept.name,
                        data: dept,
                      }
                    : null;
                })
                .filter(Boolean)}
              onChange={handleDepartmentChange}
              isClearable
              placeholder="Add departments…"
              styles={customStyles}
            />
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="text-label">Sub-Departments</Form.Label>
            <Select
              isMulti
              options={subOptions.map((sd) => ({
                value: sd.id || sd.sub_department_id,
                label: sd.name,
                data: sd,
              }))}
              value={selectedSubDepartments
                .map((id) => {
                  const sd = subOptions.find((s) => (s.id || s.sub_department_id) === id);
                  return sd ? { value: sd.id || sd.sub_department_id, label: sd.name, data: sd } : null;
                })
                .filter(Boolean)}
              onChange={handleSubDepartmentChange}
              isClearable
              placeholder="Add subDepartments…"
              styles={customStyles}
            />
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="text-label">Users</Form.Label>
            <Select
              isMulti
              options={availableUsers.map((user) => ({
                value: user.id || user.CorporateUserId,
                label: `${user.full_name || user.FullName || user.name}${
                  user.department_name || user.departmentName || user.branch_name || user.branchName
                    ? ` (${user.department_name || user.departmentName || ''}${
                        (user.department_name || user.departmentName) &&
                        (user.branch_name || user.branchName)
                          ? ', '
                          : ''
                      }${user.branch_name || user.branchName || ''})`
                    : ''
                }`,
                data: user,
              }))}
              value={selectedUsers
                .map((id) => {
                  const user = availableUsers.find((u) => userListIdEquals(u, id));
                  return user
                    ? {
                        value: user.id || user.CorporateUserId,
                        label: `${user.full_name || user.FullName || user.name}${
                          user.department_name ||
                          user.departmentName ||
                          user.branch_name ||
                          user.branchName
                            ? ` (${user.department_name || user.departmentName || ''}${
                                (user.department_name || user.departmentName) &&
                                (user.branch_name || user.branchName)
                                  ? ', '
                                  : ''
                              }${user.branch_name || user.branchName || ''})`
                            : ''
                        }`,
                        data: user,
                      }
                    : null;
                })
                .filter(Boolean)}
              onChange={handleUserChange}
              isClearable
              isSearchable
              placeholder="Add people…"
              styles={customStyles}
            />
          </Form.Group>
        </Col>

        <Col xs={12} md={6} className="credential-access-section__subblock">
          <Form.Label className="credential-access-section__label-block d-block mb-2">
            Scope of Access
          </Form.Label>
          <div className="credential-access-scope-items d-flex flex-column gap-3">
            {selectedUsers.length > 0 ? (
              <div className="credential-access-scope-of-access-users-nested">
                <span className="credential-access-scope-label">Users</span>
                <div className="d-flex flex-column gap-2">
                  {selectedUsers.map((uid) => {
                    const user = availableUsers.find((u) => userListIdEquals(u, uid));
                    const label =
                      user?.full_name || user?.FullName || user?.name || `user #${uid}`;
                    const level = userAccessById[uid] || userAccessById[String(uid)] || 'view';
                    return (
                      <div key={uid} className="credential-access-row-card">
                        <span className="credential-access-row-card__name">{label}</span>
                        <Form.Select
                          size="sm"
                          className="credential-access-level-select"
                          value={level}
                          onChange={(ev) =>
                            onUserAccessLevelChange && onUserAccessLevelChange(uid, ev.target.value)
                          }
                          aria-label={`Access level for ${label}`}
                        >
                          {ACCESS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : selectedBranches.length === 0 &&
              selectedDepartments.length === 0 &&
              selectedSubDepartments.length === 0 ? (
              <p className="credential-access-section__muted-note mb-0">
                Choose branch, department, and/or subDepartment above, then set access for each line that
                applies. Only the <strong>narrowest</strong> level you use is saved: subDepartments override
                departments and branches. Add people under <strong>Users</strong> to grant access only to
                named users.
              </p>
            ) : (
              <React.Fragment>
                {showBranchScopeLevels && (
                  <div>
                    <span className="credential-access-scope-label">Branches</span>
                    <div className="d-flex flex-column gap-2">
                      {selectedBranches.map((bid) => {
                        const branch = branches.find((b) => b.id === bid);
                        const label = branch?.name || `branch #${bid}`;
                        const level =
                          branchAccessById[bid] || branchAccessById[String(bid)] || 'view';
                        return (
                          <div key={bid} className="credential-access-row-card">
                            <span className="credential-access-row-card__name">{label}</span>
                            <Form.Select
                              size="sm"
                              className="credential-access-level-select"
                              value={level}
                              onChange={(ev) =>
                                onBranchAccessLevelChange &&
                                onBranchAccessLevelChange(bid, ev.target.value)
                              }
                              aria-label={`Access level for ${label}`}
                            >
                              {SCOPE_ACCESS_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {showDepartmentScopeLevels && (
                  <div>
                    <span className="credential-access-scope-label">Departments</span>
                    <div className="d-flex flex-column gap-2">
                      {selectedDepartments.map((did) => {
                        const dept = deptOptions.find((d) => d.id === did);
                        const label = dept
                          ? dept.branch_name
                            ? `${dept.name} (${dept.branch_name})`
                            : dept.name
                          : `department #${did}`;
                        const level =
                          departmentAccessById[did] ||
                          departmentAccessById[String(did)] ||
                          'view';
                        return (
                          <div key={did} className="credential-access-row-card">
                            <span className="credential-access-row-card__name">{label}</span>
                            <Form.Select
                              size="sm"
                              className="credential-access-level-select"
                              value={level}
                              onChange={(ev) =>
                                onDepartmentAccessLevelChange &&
                                onDepartmentAccessLevelChange(did, ev.target.value)
                              }
                              aria-label={`Access level for ${label}`}
                            >
                              {SCOPE_ACCESS_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {showSubScopeLevels && (
                  <div>
                    <span className="credential-access-scope-label">Sub-Departments</span>
                    <div className="d-flex flex-column gap-2">
                      {selectedSubDepartments.map((sid) => {
                        const sd = subOptions.find((s) => (s.id || s.sub_department_id) === sid);
                        const label = sd?.name || `subDepartment #${sid}`;
                        const level =
                          subDepartmentAccessById[sid] ||
                          subDepartmentAccessById[String(sid)] ||
                          'view';
                        return (
                          <div key={sid} className="credential-access-row-card">
                            <span className="credential-access-row-card__name">{label}</span>
                            <Form.Select
                              size="sm"
                              className="credential-access-level-select"
                              value={level}
                              onChange={(ev) =>
                                onSubDepartmentAccessLevelChange &&
                                onSubDepartmentAccessLevelChange(sid, ev.target.value)
                              }
                              aria-label={`Access level for ${label}`}
                            >
                              {SCOPE_ACCESS_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </React.Fragment>
            )}
          </div>
        </Col>
        </Row>
      ) : null}
    </div>
  );
};

export default CredentialAccessSection;
